import { useRef } from "react";
import { AlertCircle, AlertTriangle, Info, X } from "lucide-react";
import { useDialogFocus } from "@/hooks/useDialogFocus";

type Priority = "high" | "medium" | "low";

const priorities = [
  { value: "high" as const, label: "Alta", Icon: AlertCircle },
  { value: "medium" as const, label: "Média", Icon: AlertTriangle },
  { value: "low" as const, label: "Baixa", Icon: Info },
];

export function CheckinAlertDialog({
  open,
  message,
  priority,
  pending,
  error,
  onMessageChange,
  onPriorityChange,
  onClose,
  onConfirm,
}: {
  open: boolean;
  message: string;
  priority: Priority;
  pending: boolean;
  error: string | null;
  onMessageChange: (message: string) => void;
  onPriorityChange: (priority: Priority) => void;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const dialogRef = useRef<HTMLElement>(null);
  const messageRef = useRef<HTMLTextAreaElement>(null);
  useDialogFocus(dialogRef, onClose, {
    enabled: open,
    initialFocusRef: messageRef,
  });
  if (!open) return null;

  return (
    <div
      className="checkin-alert-layer"
      onMouseDown={pending ? undefined : onClose}
    >
      <section
        ref={dialogRef}
        className="checkin-alert-dialog detail-modal alert-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="checkin-alert-title"
        tabIndex={-1}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="detail-modal-head">
          <div>
            <h2 id="checkin-alert-title">Criar alerta</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={pending}
            aria-label="Fechar alerta"
          >
            <X size={18} />
          </button>
        </header>
        <div className="detail-modal-body">
          <div className="field">
            <span>Prioridade</span>
            <div
              className="priority-options"
              role="radiogroup"
              aria-label="Prioridade"
            >
              {[...priorities].reverse().map(({ value, label, Icon }) => (
                <label key={value} className={`priority-option ${value}`}>
                  <input
                    type="radio"
                    name="checkin-alert-priority"
                    checked={priority === value}
                    onChange={() => onPriorityChange(value)}
                    disabled={pending}
                  />
                  <Icon size={22} /> {label}
                </label>
              ))}
            </div>
          </div>
          <label className="field">
            <span>Mensagem</span>
            <textarea
              ref={messageRef}
              rows={5}
              value={message}
              placeholder="Descreva o problema encontrado..."
              onChange={(event) => onMessageChange(event.target.value)}
            />
          </label>
          {error ? (
            <p className="checkin-alert-error" role="alert">
              {error}
            </p>
          ) : null}
        </div>
        <footer className="detail-modal-foot">
          <button type="button" onClick={onClose} disabled={pending}>
            Cancelar
          </button>
          <button
            type="button"
            className="primary"
            onClick={() => {
              dialogRef.current?.focus();
              onConfirm();
            }}
            disabled={pending || !message.trim()}
          >
            <AlertTriangle size={16} />{" "}
            {pending ? "Enviando…" : "Enviar alerta"}
          </button>
        </footer>
      </section>
    </div>
  );
}
